import { useState, useEffect, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/integrations/supabase/client';
import { getCurrentMention } from '@/utils/mentions';
import { cn } from '@/lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

interface Profile {
  username: string;
  display_name: string;
  avatar_url: string;
}

interface MentionInputProps {
  value: string;
  onChange: (value: string) => void;
  onKeyPress?: (e: React.KeyboardEvent) => void;
  placeholder?: string;
  className?: string;
  maxLength?: number;
  disabled?: boolean;
  multiline?: boolean;
  'aria-label'?: string;
}

export function MentionInput({
  value,
  onChange,
  onKeyPress,
  placeholder,
  className,
  maxLength,
  disabled,
  multiline = false,
  'aria-label': ariaLabel,
}: MentionInputProps) {
  const [suggestions, setSuggestions] = useState<Profile[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  useEffect(() => {
    const loadSuggestions = async (query: string) => {
      if (!query || query.length < 1) {
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('username, display_name, avatar_url')
          .or(`username.ilike.%${query}%,display_name.ilike.%${query}%`)
          .limit(5);

        if (error) throw error;

        setSuggestions(data || []);
        setShowSuggestions(data && data.length > 0);
        setSelectedIndex(0);
      } catch (error) {
        console.error('Error loading suggestions:', error);
        setSuggestions([]);
        setShowSuggestions(false);
      }
    };

    const cursorPosition = inputRef.current?.selectionStart || 0;
    const currentMention = getCurrentMention(value, cursorPosition);

    if (currentMention !== null) {
      loadSuggestions(currentMention);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [value]);

  const insertMention = (username: string) => {
    const cursorPosition = inputRef.current?.selectionStart || 0;
    const textBeforeCursor = value.slice(0, cursorPosition);
    const textAfterCursor = value.slice(cursorPosition);
    const lastAtSymbol = textBeforeCursor.lastIndexOf('@');

    if (lastAtSymbol !== -1) {
      const newValue = 
        value.slice(0, lastAtSymbol) + 
        `@${username} ` + 
        textAfterCursor;
      onChange(newValue);
      setShowSuggestions(false);

      // Set cursor position after the mention
      setTimeout(() => {
        const newCursorPos = lastAtSymbol + username.length + 2;
        inputRef.current?.setSelectionRange(newCursorPos, newCursorPos);
        inputRef.current?.focus();
      }, 0);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (showSuggestions && suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % suggestions.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + suggestions.length) % suggestions.length);
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        insertMention(suggestions[selectedIndex].username);
        return;
      } else if (e.key === 'Escape') {
        setShowSuggestions(false);
      }
    }

    onKeyPress?.(e);
  };

  const InputComponent = multiline ? Textarea : Input;

  return (
    <div className="relative w-full">
      <InputComponent
        ref={inputRef as any}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={className}
        maxLength={maxLength}
        disabled={disabled}
        aria-label={ariaLabel}
      />

      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute bottom-full left-0 right-0 mb-1 bg-popover border border-border rounded-md shadow-lg max-h-48 overflow-y-auto z-50">
          {suggestions.map((profile, index) => (
            <button
              key={profile.username}
              type="button"
              className={cn(
                "w-full flex items-center gap-3 px-3 py-2 hover:bg-accent transition-colors",
                index === selectedIndex && "bg-accent"
              )}
              onClick={() => insertMention(profile.username)}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <Avatar className="w-8 h-8">
                <AvatarImage src={profile.avatar_url} />
                <AvatarFallback>{profile.username[0]}</AvatarFallback>
              </Avatar>
              <div className="flex-1 text-left">
                <div className="text-sm font-medium">{profile.display_name || profile.username}</div>
                <div className="text-xs text-muted-foreground">@{profile.username}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
